import { useEffect, useState, useMemo } from "react";
import {
  Link,
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Sparkles,
  ShoppingBag,
  Trash2,
  Minus,
  Plus,
} from "lucide-react";
import { toast } from "sonner";
import ProductCard from "../components/ProductCard";
import AddressForm from "../components/AddressForm";
import {
  apiFetch,
  money,
  resolveImageUrl,
  mainImage,
} from "../lib/api";

/* =========================================================
   SEO
   ========================================================= */

const SITE_URL = "https://www.gmkhandicrafts.in";

const DEFAULT_SITE_TITLE =
  "GMK Handicrafts Emporium | Authentic Kashmiri Handicrafts & Pashmina";

const DEFAULT_SITE_DESCRIPTION =
  "Discover authentic Kashmiri handicrafts, Pashmina, Tosha and Tusha shawls, handcrafted with the timeless artistry and heritage of Kashmir.";

function setMeta(name, content) {
  if (!content) return;

  let meta = document.head.querySelector(
    `meta[name="${name}"]`
  );

  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", name);
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", content);
}

function setPropertyMeta(property, content) {
  if (!content) return;

  let meta = document.head.querySelector(
    `meta[property="${property}"]`
  );

  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("property", property);
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", content);
}

function setCanonical(url) {
  let canonical = document.head.querySelector(
    'link[rel="canonical"]'
  );

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }

  canonical.setAttribute("href", url);
}

function normalizeSeoImage(image) {
  if (!image) return "";

  try {
    return resolveImageUrl(image) || image;
  } catch {
    return image;
  }
}

function isNoIndexRobots(value) {
  return String(value || "")
    .toLowerCase()
    .startsWith("noindex");
}

function buildCanonicalUrl(value, fallbackPath = "/") {
  const raw = String(value || fallbackPath).trim();

  if (
    raw.startsWith("http://") ||
    raw.startsWith("https://")
  ) {
    return raw;
  }

  return `${SITE_URL}${
    raw.startsWith("/") ? raw : `/${raw}`
  }`;
}

function setPageSEO({
  title = DEFAULT_SITE_TITLE,
  description = DEFAULT_SITE_DESCRIPTION,
  path = "/",
  noindex = false,
  image = "",
  robots = "",
  ogTitle = "",
  ogDescription = "",
}) {
  const cleanTitle = String(
    title || DEFAULT_SITE_TITLE
  ).trim();

  const cleanDescription = String(
    description || DEFAULT_SITE_DESCRIPTION
  )
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 320);

  const canonicalUrl = buildCanonicalUrl(
    path,
    "/"
  );

  const finalRobots =
    robots ||
    (noindex
      ? "noindex, follow, max-image-preview:large"
      : "index, follow, max-image-preview:large");

  const normalizedImage =
    normalizeSeoImage(image);

  document.title = cleanTitle;

  setMeta(
    "description",
    cleanDescription
  );

  setMeta(
    "robots",
    finalRobots
  );

  setMeta(
    "googlebot",
    finalRobots
  );

  setCanonical(canonicalUrl);

  setPropertyMeta(
    "og:type",
    "website"
  );

  setPropertyMeta(
    "og:site_name",
    "GMK Handicrafts Emporium"
  );

  setPropertyMeta(
    "og:title",
    String(
      ogTitle || cleanTitle
    ).trim()
  );

  setPropertyMeta(
    "og:description",
    String(
      ogDescription ||
        cleanDescription
    )
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 320)
  );

  setPropertyMeta(
    "og:url",
    canonicalUrl
  );

  if (normalizedImage) {
    setPropertyMeta(
      "og:image",
      normalizedImage
    );
  }

  setMeta(
    "twitter:card",
    normalizedImage
      ? "summary_large_image"
      : "summary"
  );

  setMeta(
    "twitter:title",
    String(
      ogTitle || cleanTitle
    ).trim()
  );

  setMeta(
    "twitter:description",
    String(
      ogDescription ||
        cleanDescription
    )
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 320)
  );

  if (normalizedImage) {
    setMeta(
      "twitter:image",
      normalizedImage
    );
  }
}

/*
 * Loads the SEO settings created in:
 * Admin → SEO Manager
 *
 * This deliberately fails silently so that the website
 * keeps using its built-in SEO if the SEO settings cannot
 * be loaded for any reason.
 */
async function fetchPublicSEO(
  scopeType,
  scopeKey
) {
  try {
    const data = await apiFetch(
      `/public/seo?scope_type=${encodeURIComponent(
        scopeType
      )}&scope_key=${encodeURIComponent(
        scopeKey
      )}`
    );

    if (!data) return null;

    return data;
  } catch {
    return null;
  }
}

function applyManagedSEO(
  seo,
  fallback = {}
) {
  const title =
    seo?.title ||
    fallback.title ||
    DEFAULT_SITE_TITLE;

  const description =
    seo?.description ||
    fallback.description ||
    DEFAULT_SITE_DESCRIPTION;

  const path =
    seo?.canonical_url ||
    fallback.path ||
    "/";

  const robots =
    seo?.robots ||
    "";

  const noindex =
    isNoIndexRobots(robots) ||
    Boolean(fallback.noindex);

  const image =
    seo?.og_image ||
    fallback.image ||
    "";

  setPageSEO({
    title,
    description,
    path,
    noindex,
    image,
    robots,
    ogTitle:
      seo?.og_title ||
      fallback.ogTitle ||
      "",
    ogDescription:
      seo?.og_description ||
      fallback.ogDescription ||
      "",
  });
}

function makeCategorySEO(category) {
  if (!category) {
    return {
      title:
        "Kashmiri Handicrafts | GMK Handicrafts Emporium",
      description:
        "Explore authentic Kashmiri handicrafts from GMK Handicrafts Emporium, thoughtfully curated across shawls, carpets and other traditional collections.",
    };
  }

  const name =
    category.name ||
    "Kashmiri Handicrafts";

  const description =
    category.description ||
    `Explore authentic ${name} from GMK Handicrafts Emporium, crafted with the timeless artistry of Kashmir.`;

  return {
    title:
      `${name} | Authentic Kashmiri Handicrafts | GMK Handicrafts Emporium`,
    description,
  };
}

function makeSubcategorySEO(
  subcategory,
  parentCategory
) {
  const subName =
    subcategory?.name ||
    "Collection";

  const parentName =
    parentCategory?.name ||
    "Kashmiri Handicrafts";

  return {
    title:
      `${subName} | ${parentName} | GMK Handicrafts Emporium`,
    description:
      subcategory?.description ||
      `Explore ${subName} from the ${parentName} collection at GMK Handicrafts Emporium.`,
  };
}

/*
 * Returns the canonical public URL for a
 * top-level catalogue category.
 *
 * Shawls keeps its established /shawls URL.
 * Every other top-level category uses the
 * generic /collection/:category route.
 */
function getCategoryPath(category) {
  if (!category?.slug) {
    return "/";
  }

  if (
    String(category.slug).toLowerCase() ===
    "shawls"
  ) {
    return "/shawls";
  }

  return `/collection/${category.slug}`;
}

function makeProductSEO(product) {
  const productName =
    product?.name ||
    "Kashmiri Handicraft";

  const defaultDescription =
    product?.short_description ||
    product?.description ||
    `Discover ${productName}, an authentic Kashmiri handicraft from GMK Handicrafts Emporium.`;

  return {
    title:
      product?.seo_title?.trim() ||
      `${productName} | GMK Handicrafts Emporium`,
    description:
      product?.seo_description?.trim() ||
      defaultDescription,
  };
}

export function Home({
  cart,
  addItem,
  wishlist,
  toggleWish,
}) {
  const [products, setProducts] =
    useState([]);

  const [cats, setCats] =
    useState([]);

  const [settings, setSettings] =
    useState({});

  useEffect(() => {
    apiFetch(
      "/products?featured=true&limit=8"
    )
      .then((data) => {
        setProducts(
          Array.isArray(data)
            ? data
            : []
        );
      })
      .catch(() => {});

    apiFetch("/categories")
      .then((data) => {
        setCats(
          Array.isArray(data)
            ? data.filter(
                (category) =>
                  category?.hidden !== true
              )
            : []
        );
      })
      .catch(() => {});

    apiFetch("/site-settings")
      .then(setSettings)
      .catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;

    async function loadSEO() {
      const seo =
        await fetchPublicSEO(
          "page",
          "home"
        );

      if (!active) return;

      applyManagedSEO(
        seo,
        {
          title:
            "GMK Handicrafts Emporium | Authentic Kashmiri Handicrafts & Pashmina",
          description:
            "Discover authentic Kashmiri handicrafts, Pashmina, Tosha and Tusha shawls, handcrafted with the timeless artistry and heritage of Kashmir.",
          path: "/",
        }
      );
    }

    loadSEO();

    return () => {
      active = false;
    };
  }, []);

  const hero =
    resolveImageUrl(
      settings.hero_image
    ) || "";

  const featureCategory =
    cats.find(
      (category) =>
        Boolean(category?.banner)
    ) ||
    cats[0] ||
    null;

  const heroHeadline =
    settings.hero_headline ||
    "The Art of Kashmir, Woven with Elegance.";

  const heroSubheadline =
    settings.hero_subheadline ||
    "Discover timeless Kashmiri craftsmanship through a considered collection of exquisite handicrafts.";

  const heroPrimaryCTA =
    settings.hero_cta_primary ||
    "Explore our collections";

  return (
    <>
      <section
        className="hero"
        style={{
          backgroundImage: `url(${hero})`,
        }}
        data-testid="hero-section"
      >
        <div className="hero-overlay">
          <span className="kicker">
            The Kashmir edit
          </span>

          <h1>
            {heroHeadline}
          </h1>

          <p>
            {heroSubheadline}
          </p>

          <div className="hero-buttons">
            <a
              className="button button-light"
              href="#collections"
              data-testid="hero-explore-button"
            >
              {heroPrimaryCTA}{" "}
              <ArrowRight size={16} />
            </a>

            <Link
              className="text-link light"
              to="/about"
              data-testid="hero-story-button"
            >
              {settings.hero_cta_secondary ||
                "Discover our story"}
            </Link>
          </div>
        </div>

        <div className="scroll-cue">
          Scroll to explore <span>↓</span>
        </div>
      </section>

      <section className="intro section">
        <div>
          <span className="eyebrow">
            A considered collection
          </span>

          <h2>
            Quiet luxury, made
            <br />
            <i>by hand.</i>
          </h2>
        </div>

        <p className="intro-copy">
          From the valleys of Kashmir, a curated edit
          of pieces made to be lived in and treasured.
          Each weave carries the patience, skill and
          spirit of its maker.
        </p>
      </section>

      <section
        className="categories section"
        id="collections"
      >
        <div className="section-top">
          <div>
            <span className="eyebrow">
              Explore our collections
            </span>

            <h2>
              The art of Kashmir,
              <br />
              <i>across collections.</i>
            </h2>
          </div>

          {cats.length > 0 && (
            <Link
              className="text-link"
              to={getCategoryPath(
                cats[0]
              )}
              data-testid="view-all-categories"
            >
              Explore collections{" "}
              <ArrowRight size={16} />
            </Link>
          )}
        </div>

        <div className="category-grid">
          {cats.map((c) => {
            const categoryPath =
              getCategoryPath(c);

            return (
              <Link
                className="category-tile"
                to={categoryPath}
                key={c.id}
                data-testid={`category-${c.slug}`}
              >
                <img
                  src={
                    resolveImageUrl(
                      c.thumbnail
                    ) || hero
                  }
                  alt={
                    c.name
                      ? `${c.name} — GMK Handicrafts Emporium`
                      : "GMK Handicrafts collection"
                  }
                />

                <div>
                  <span>
                    {c.description ||
                      "Discover this collection"}
                  </span>

                  <h3>{c.name}</h3>

                  <ArrowRight size={18} />
                </div>
              </Link>
            );
          })}

          {!cats.length && (
            <div className="empty">
              <h2>
                Our collections are
                being prepared.
              </h2>

              <p>
                Please check back soon
                to explore the art of
                Kashmir.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="feature-band">
        <div className="feature-copy">
          <span className="eyebrow">
            The GMK standard
          </span>

          <h2>
            Craftsmanship
            <br />
            <i>you can feel.</i>
          </h2>

          <p>
            We believe the most beautiful things carry
            evidence of the human hand. Our edit honours
            material, patience and the subtle
            imperfections that make each piece singular.
          </p>

          <Link
            className="button button-dark"
            to="/about"
            data-testid="craft-story-button"
          >
            Our story{" "}
            <ArrowRight size={16} />
          </Link>
        </div>

        <div className="feature-image">
          <img
            src={
              resolveImageUrl(
                featureCategory?.banner
              ) || hero
            }
            alt={
              featureCategory?.name
                ? `${featureCategory.name} collection`
                : "Kashmiri craft detail"
            }
          />
        </div>
      </section>

      {products.length > 0 && (
        <section className="products-section section">
          <div className="section-top">
            <div>
              <span className="eyebrow">
                A few favourites
              </span>

              <h2>
                From the <i>atelier.</i>
              </h2>
            </div>

            {cats.length > 0 && (
              <Link
                className="text-link"
                to={getCategoryPath(
                  cats[0]
                )}
                data-testid="view-all-products"
              >
                Shop the full edit{" "}
                <ArrowRight size={16} />
              </Link>
            )}
          </div>

          <div className="product-grid">
            {products.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                onAdd={addItem}
                onToggleWish={toggleWish}
                inWishlist={wishlist.includes(
                  p.id
                )}
              />
            ))}
          </div>
        </section>
      )}

      <section className="trust section">
        <div>
          <ShieldCheck />

          <span>
            <b>Thoughtful service</b>
            Questions answered with care
          </span>
        </div>

        <div>
          <Sparkles />

          <span>
            <b>Considered craftsmanship</b>
            Pieces chosen for their character
          </span>
        </div>

        <div>
          <ShoppingBag />

          <span>
            <b>Safe checkout</b>
            Online payment coming soon
          </span>
        </div>
      </section>
    </>
  );
}

/* =========================================================
   CATEGORY / SEARCH
   ========================================================= */

export function Category({
  addItem,
  wishlist,
  toggleWish,
  routeType = "shawls-root",
}) {
  const {
    category: categoryParam,
    subcategory: subcategoryParam,
  } = useParams();

  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(
    location.search
  ).get("search");

  const querySubcategory = new URLSearchParams(
    location.search
  ).get("subcategory");

  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [subcats, setSubcats] = useState([]);
  const [sort, setSort] = useState("featured");
  const [selectedSubcategory, setSelectedSubcategory] =
    useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [material, setMaterial] = useState("");
  const [inStockOnly, setInStockOnly] = useState(false);
  useEffect(() => {
    let active = true;

    apiFetch("/categories")
      .then((data) => {
        if (!active) return;

        setCats(
          Array.isArray(data)
            ? data.filter(
                (category) =>
                  category?.hidden !== true
              )
            : []
        );
      })
      .catch(() => {
        if (active) {
          setCats([]);
        }
      });

    return () => {
      active = false;
    };
  }, []);
  /*
   * Resolve the top-level parent category.
   *
   * /shawls
   *     -> Shawls
   *
   * /shawls/pashmina
   *     -> Shawls + Pashmina
   *
   * /collection/kaleen
   *     -> KALEEN
   *
   * /collection/kaleen/some-subcategory
   *     -> KALEEN + that subcategory
   */
  const parentCategory = useMemo(() => {
    if (!cats.length) {
      return null;
    }

    if (routeType === "shawls-root") {
      return (
        cats.find(
          (cat) =>
            String(cat.slug || "").toLowerCase() ===
            "shawls"
        ) || null
      );
    }

    if (
      routeType === "collection" ||
      routeType === "legacy"
    ) {
      const directCategory = cats.find(
        (cat) =>
          String(cat.slug || "").toLowerCase() ===
          String(categoryParam || "").toLowerCase()
      );

      if (directCategory) {
        return directCategory;
      }

      /*
       * /shawls/pashmina is the established
       * legacy Shawls subcategory URL.
       */
      if (
        routeType === "legacy" ||
        String(categoryParam || "").trim()
      ) {
        return (
          cats.find(
            (cat) =>
              String(cat.slug || "").toLowerCase() ===
              "shawls"
          ) || null
        );
      }
    }

    return null;
  }, [
    cats,
    routeType,
    categoryParam,
  ]);

  /*
   * Load visible subcategories belonging ONLY
   * to the selected parent category.
   */
  useEffect(() => {
    let active = true;

    setSubcats([]);

    if (!parentCategory?.id) {
      return () => {
        active = false;
      };
    }

    apiFetch(
      `/subcategories?category_id=${encodeURIComponent(
        parentCategory.id
      )}`
    )
      .then((data) => {
        if (!active) return;

        setSubcats(
          Array.isArray(data)
            ? data.filter(
                (subcategory) =>
                  subcategory?.hidden !== true
              )
            : []
        );
      })
      .catch(() => {
        if (active) {
          setSubcats([]);
        }
      });

    return () => {
      active = false;
    };
  }, [parentCategory?.id]);

  /*
   * Determine the requested subcategory.
   *
   * Supported forms:
   * /shawls/pashmina
   * /collection/kaleen?subcategory=...
   * /collection/kaleen/subcategory
   */
  useEffect(() => {
    if (!parentCategory || !subcats.length) {
      setSelectedSubcategory("");
      return;
    }

    const requestedSlug =
      subcategoryParam ||
      querySubcategory ||
      (
        routeType === "legacy" &&
        String(parentCategory.slug || "").toLowerCase() ===
          "shawls"
          ? categoryParam
          : ""
      );

    if (!requestedSlug) {
      setSelectedSubcategory("");
      return;
    }

    const matchingSubcategory = subcats.find(
      (subcategory) =>
        String(subcategory.slug || "").toLowerCase() ===
        String(requestedSlug || "").toLowerCase()
    );

    setSelectedSubcategory(
      matchingSubcategory?.slug || ""
    );
  }, [
    parentCategory,
    subcats,
    routeType,
    categoryParam,
    subcategoryParam,
    querySubcategory,
  ]);

  /*
   * Load products using the NEW hierarchy fields.
   *
   * This is the important repair:
   *
   * category_id = parent category ID
   *
   * We no longer depend on the old category-name
   * relationship to discover the catalogue.
   */
  useEffect(() => {
    let active = true;

    async function loadProducts() {
      if (query) {
        const params = new URLSearchParams();

        params.set("q", query);
        params.set("sort", sort);

        try {
          const data = await apiFetch(
            `/products?${params.toString()}`
          );

          if (!active) return;

          setProducts(
            Array.isArray(data) ? data : []
          );
        } catch {
          if (!active) return;

          setProducts([]);
          toast.error(
            "Unable to load the collection"
          );
        }

        return;
      }

      if (!parentCategory?.id) {
        setProducts([]);
        return;
      }

      const params = new URLSearchParams();

      /*
       * Use category_id first.
       *
       * The backend hierarchy is now:
       * Category -> Subcategory -> Product.
       */
      params.set(
        "category_id",
        parentCategory.id
      );

      params.set("sort", sort);

      try {
        const data = await apiFetch(
          `/products?${params.toString()}`
        );

        if (!active) return;

        setProducts(
          Array.isArray(data) ? data : []
        );
      } catch {
        if (!active) return;

        setProducts([]);
        toast.error(
          "Unable to load the collection"
        );
      }
    }

    loadProducts();

    return () => {
      active = false;
    };
  }, [
    query,
    parentCategory?.id,
    sort,
  ]);

  /*
   * Filter the already-loaded parent products
   * by the selected subcategory.
   *
   * category_id/subcategory_id are authoritative.
   * The name fallback preserves compatibility with
   * any older product records.
   */
  const categoryProducts = useMemo(() => {
    if (!selectedSubcategory) {
      return products;
    }

    const subcategory = subcats.find(
      (subcat) =>
        String(subcat.slug || "").toLowerCase() ===
        String(selectedSubcategory || "").toLowerCase()
    );

    if (!subcategory) {
      return products;
    }

    return products.filter((product) => {
      if (product.subcategory_id) {
        return (
          product.subcategory_id ===
          subcategory.id
        );
      }

      return (
        String(
          product.subcategory_name || ""
        ).trim().toLowerCase() ===
        String(
          subcategory.name || ""
        ).trim().toLowerCase()
      );
    });
  }, [
    products,
    subcats,
    selectedSubcategory,
  ]);

  const activeSubcategory = useMemo(() => {
    return (
      subcats.find(
        (subcategory) =>
          String(subcategory.slug || "").toLowerCase() ===
          String(selectedSubcategory || "").toLowerCase()
      ) || null
    );
  }, [
    subcats,
    selectedSubcategory,
  ]);

  const materials = useMemo(() => {
    return [
      ...new Set(
        categoryProducts
          .map(
            (product) =>
              product.material
          )
          .filter(Boolean)
      ),
    ];
  }, [categoryProducts]);

  const visible = useMemo(() => {
    return categoryProducts.filter(
      (product) =>
        (!maxPrice ||
          product.price <= Number(maxPrice)) &&
        (!material ||
          product.material === material) &&
        (!inStockOnly ||
          (product.stock ?? 0) > 0)
    );
  }, [
    categoryProducts,
    maxPrice,
    material,
    inStockOnly,
  ]);

  const banner =
    resolveImageUrl(
      activeSubcategory?.banner
    ) ||
    resolveImageUrl(
      parentCategory?.banner
    ) ||
    "";

  const pageTitle = query
    ? `Search results for "${query}"`
    : activeSubcategory
    ? activeSubcategory.name
    : parentCategory
    ? `All ${parentCategory.name}`
    : "Collection";

  const pageDescription = query
    ? `Search results for "${query}" in the GMK Handicrafts Emporium collection.`
    : activeSubcategory?.description ||
      parentCategory?.description ||
      `Explore the ${
        parentCategory?.name ||
        "GMK Handicrafts"
      } collection.`;

  /*
   * Dynamic SEO.
   */
  useEffect(() => {
    let active = true;

    async function loadSEO() {
      if (query) {
        if (!active) return;

        setPageSEO({
          title:
            `Search results for "${query}" | GMK Handicrafts Emporium`,
          description:
            `Search results for "${query}" in the GMK Handicrafts Emporium collection.`,
          path:
            `${location.pathname}${location.search}`,
          noindex: true,
        });

        return;
      }

      let seo = null;

      if (activeSubcategory) {
        seo = await fetchPublicSEO(
          "subcategory",
          activeSubcategory.slug
        );

        if (!seo) {
          seo = await fetchPublicSEO(
            "category",
            activeSubcategory.slug
          );
        }

        if (!seo) {
          seo = await fetchPublicSEO(
            "page",
            activeSubcategory.slug
          );
        }
      }

      if (!seo && parentCategory) {
        seo = await fetchPublicSEO(
          "category",
          parentCategory.slug
        );

        if (!seo) {
          seo = await fetchPublicSEO(
            "page",
            parentCategory.slug
          );
        }
      }

      if (!active) return;

      const fallback = activeSubcategory
        ? makeSubcategorySEO(
            activeSubcategory,
            parentCategory
          )
        : makeCategorySEO(
            parentCategory
          );

      let canonicalPath =
        getCategoryPath(
          parentCategory
        );

      /*
       * Preserve the established Shawls URLs.
       */
      if (
        activeSubcategory &&
        String(
          parentCategory?.slug || ""
        ).toLowerCase() ===
          "shawls"
      ) {
        canonicalPath =
          `/shawls/${activeSubcategory.slug}`;
      }

      /*
       * Generic category subcategories use
       * the clean nested route when available.
       */
      if (
        activeSubcategory &&
        String(
          parentCategory?.slug || ""
        ).toLowerCase() !==
          "shawls"
      ) {
        canonicalPath =
          `/collection/${parentCategory.slug}/${activeSubcategory.slug}`;
      }

      applyManagedSEO(
        seo,
        {
          title: fallback.title,
          description:
            fallback.description,
          path: canonicalPath,
          image: banner,
        }
      );
    }

    loadSEO();

    return () => {
      active = false;
    };
  }, [
    query,
    activeSubcategory,
    parentCategory,
    banner,
    location.pathname,
    location.search,
  ]);

  /*
   * Select parent or subcategory.
   */
  const selectSubcategory = (
    subcategorySlug
  ) => {
    if (!subcategorySlug) {
      navigate(
        getCategoryPath(
          parentCategory
        )
      );

      return;
    }

    if (
      String(
        parentCategory?.slug || ""
      ).toLowerCase() ===
      "shawls"
    ) {
      navigate(
        `/shawls/${subcategorySlug}`
      );

      return;
    }

    navigate(
      `/collection/${parentCategory.slug}/${subcategorySlug}`
    );
  };

    const breadcrumbItems = useMemo(() => {
    const items = [
      {
        name: "Home",
        url: `${SITE_URL}/`,
      },
    ];

    if (query) {
      items.push({
        name: "Search",
        url: `${SITE_URL}${location.pathname}${location.search}`,
      });

      return items;
    }

    if (!parentCategory) {
      return items;
    }

    const parentPath =
      getCategoryPath(parentCategory);

    items.push({
      name: parentCategory.name,
      url: `${SITE_URL}${parentPath}`,
    });

    if (activeSubcategory) {
      const subcategoryPath =
        String(
          parentCategory.slug || ""
        ).toLowerCase() === "shawls"
          ? `/shawls/${activeSubcategory.slug}`
          : `/collection/${parentCategory.slug}/${activeSubcategory.slug}`;

      items.push({
        name: activeSubcategory.name,
        url: `${SITE_URL}${subcategoryPath}`,
      });
    }

    return items;
  }, [
    query,
    location.pathname,
    location.search,
    parentCategory,
    activeSubcategory,
  ]);

  useEffect(() => {
    const scriptId =
      "gmk-category-breadcrumb-schema";

    let script = document.getElementById(
      scriptId
    );

    if (!script) {
      script = document.createElement(
        "script"
      );
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }

    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement:
        breadcrumbItems.map(
          (item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            item: item.url,
          })
        ),
    });

    return () => {
      const currentScript =
        document.getElementById(
          scriptId
        );

      if (currentScript) {
        currentScript.remove();
      }
    };
  }, [breadcrumbItems]);

  return (
    <main className="page">
      <nav
        aria-label="Breadcrumb"
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "18px 24px 0",
          fontSize: "0.9rem",
        }}
      >
        <ol
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "8px",
            margin: 0,
            padding: 0,
            listStyle: "none",
          }}
        >
          {breadcrumbItems.map(
            (item, index) => {
              const isLast =
                index ===
                breadcrumbItems.length - 1;

              return (
                <li
                  key={`${item.url}-${index}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  {isLast ? (
                    <span
                      aria-current="page"
                      style={{
                        opacity: 0.7,
                      }}
                    >
                      {item.name}
                    </span>
                  ) : (
                    <>
                      <Link
                        to={
                          item.url.replace(
                            SITE_URL,
                            ""
                          ) || "/"
                        }
                        style={{
                          textDecoration:
                            "none",
                        }}
                      >
                        {item.name}
                      </Link>

                      <span
                        aria-hidden="true"
                        style={{
                          opacity: 0.45,
                        }}
                      >
                        /
                      </span>
                    </>
                  )}
                </li>
              );
            }
          )}
        </ol>
      </nav>
    
      <div
        className={
          "category-header " +
          (banner
            ? "has-banner"
            : "")
        }
        style={
          banner
            ? {
                backgroundImage:
                  `linear-gradient(to bottom, rgba(43,27,27,.1), rgba(253,250,244,1)), url(${banner})`,
              }
            : {}
        }
      >
        <span className="eyebrow">
          {query
            ? "Search"
            : parentCategory?.name ||
              "GMK Handicrafts"}
        </span>

        <h1 data-testid="category-title">
          {pageTitle}
        </h1>

        <p>
          {pageDescription}
        </p>
      </div>

      {!query && (
        <div className="filterbar">
          <div className="filter-tabs">
            <button
              className={
                !selectedSubcategory
                  ? "selected"
                  : ""
              }
              onClick={() =>
                selectSubcategory("")
              }
              data-testid="filter-all"
            >
              All{" "}
              {parentCategory?.name ||
                "Collections"}
            </button>

            {subcats.map(
              (subcategory) => (
                <button
                  key={
                    subcategory.id
                  }
                  className={
                    selectedSubcategory ===
                    subcategory.slug
                      ? "selected"
                      : ""
                  }
                  onClick={() =>
                    selectSubcategory(
                      subcategory.slug
                    )
                  }
                  data-testid={`filter-${subcategory.slug}`}
                >
                  {subcategory.name}
                </button>
              )
            )}
          </div>

          <div className="filter-right">
            <select
              value={material}
              onChange={(e) =>
                setMaterial(
                  e.target.value
                )
              }
              aria-label="Material filter"
              data-testid="filter-material"
            >
              <option value="">
                All materials
              </option>

              {materials.map(
                (m) => (
                  <option
                    key={m}
                    value={m}
                  >
                    {m}
                  </option>
                )
              )}
            </select>

            <input
              type="number"
              placeholder="Max price"
              value={maxPrice}
              onChange={(e) =>
                setMaxPrice(
                  e.target.value
                )
              }
              aria-label="Max price"
              data-testid="filter-price"
            />

            <label className="in-stock">
              <input
                type="checkbox"
                checked={
                  inStockOnly
                }
                onChange={(e) =>
                  setInStockOnly(
                    e.target.checked
                  )
                }
                data-testid="filter-instock"
              />

              In stock only
            </label>

            <select
              value={sort}
              onChange={(e) =>
                setSort(
                  e.target.value
                )
              }
              data-testid="sort-products"
              aria-label="Sort"
            >
              <option value="featured">
                Featured
              </option>

              <option value="newest">
                Newest
              </option>

              <option value="price_asc">
                Price: Low to High
              </option>

              <option value="price_desc">
                Price: High to Low
              </option>

              <option value="name">
                Name
              </option>
            </select>
          </div>
        </div>
      )}

      <div className="product-grid large">
        {visible.map(
          (product) => (
            <ProductCard
              key={product.id}
              product={product}
              onAdd={addItem}
              onToggleWish={
                toggleWish
              }
              inWishlist={wishlist.includes(
                product.id
              )}
            />
          )
        )}

        {!visible.length && (
          <div className="empty">
            <h2>
              Your next favourite is
              on its way.
            </h2>

            <p>
              No pieces match this
              selection yet. Check back
              soon or browse another
              collection.
            </p>
          </div>
        )}
      </div>
    </main>
  );
}

/* =========================================================
   PRODUCT DETAIL
   ========================================================= */

export function ProductDetail({
  addItem,
}) {
  const { id } =
    useParams();

  const nav =
    useNavigate();

  const [p, setP] =
    useState(null);

  const [image, setImage] =
    useState(0);

  const [qty, setQty] =
    useState(1);

  const [related, setRelated] =
    useState([]);

  const [reviews, setReviews] =
    useState([]);

  useEffect(() => {
    apiFetch(
      `/products/${id}`
    )
      .then((data) => {
        setP(data);
        setImage(0);

        apiFetch(
          `/products?category=${encodeURIComponent(
            data.category
          )}&limit=4`
        )
          .then((all) => {
            setRelated(
              all
                .filter(
                  (x) =>
                    x.id !==
                    data.id
                )
                .slice(0, 3)
            );
          })
          .catch(() => {});

        apiFetch(
          `/reviews/${data.id}`
        )
          .then(setReviews)
          .catch(() => {});
      })
      .catch(() =>
        toast.error(
          "Product not found"
        )
      );
  }, [id]);

  useEffect(() => {
    if (!p) return;

    let active = true;

    async function loadSEO() {
      const fallback =
        makeProductSEO(p);

      /*
       * Product-level SEO is optional.
       * If an Admin SEO record exists for
       * this product, use it.
       *
       * Otherwise keep the existing
       * product SEO title/description.
       */
      const seo =
        await fetchPublicSEO(
          "product",
          id
        );

      if (!active) return;

      const productImage =
        resolveImageUrl(
          mainImage(p)
        );

      applyManagedSEO(
        seo,
        {
          title:
            fallback.title,
          description:
            fallback.description,
          path:
            `/product/${id}`,
          image:
            productImage,
        }
      );
    }

    loadSEO();

    return () => {
      active = false;
    };
  }, [p, id]);

  if (!p) {
    return (
      <div className="loading">
        Loading the collection…
      </div>
    );
  }

  const images =
    p.images &&
    p.images.length
      ? p.images
      : [{ url: "" }];

  const outOfStock =
    (p.stock ?? 0) <= 0;

  const addToCart = () => {
    addItem(
      p,
      qty
    );

    toast.success(
      "Added to your collection"
    );
  };

  const buyNow = () => {
    addToCart();
    nav("/checkout");
  };

  return (
    <main className="page product-page">
      <div className="gallery">
        <img
          src={resolveImageUrl(
            images[image]
          )}
          alt={p.name}
          data-testid="product-main-image"
        />

        <div className="thumbs">
          {images.map(
            (img, i) => (
              <button
                onClick={() =>
                  setImage(i)
                }
                className={
                  i === image
                    ? "selected"
                    : ""
                }
                key={
                  (img.path ||
                    img.url) +
                  i
                }
                data-testid={`product-thumbnail-${i}`}
                aria-label={`Image ${
                  i + 1
                }`}
              >
                <img
                  src={resolveImageUrl(
                    img
                  )}
                  alt={
                    img.alt ||
                    `${p.name} view ${
                      i + 1
                    }`
                  }
                />
              </button>
            )
          )}
        </div>
      </div>

      <div className="product-detail">
        <span className="eyebrow">
          {p.category} ·{" "}
          {p.material}
        </span>

        <h1 data-testid="product-title">
          {p.name}
        </h1>

        <div className="price-row">
          <strong className="price">
            {money(p.price)}
          </strong>

          {p.compare_at_price >
            p.price && (
            <span className="price-compare">
              {money(
                p.compare_at_price
              )}
            </span>
          )}
        </div>

        <p className="short">
          {p.short_description}
        </p>

        <p className="detail-description">
          {p.description}
        </p>

        <dl>
          {p.material && (
            <div>
              <dt>Material</dt>

              <dd>
                {p.material}
              </dd>
            </div>
          )}

          {p.dimensions && (
            <div>
              <dt>Dimensions</dt>

              <dd>
                {p.dimensions}
              </dd>
            </div>
          )}

          {p.colors?.length >
            0 && (
            <div>
              <dt>Colours</dt>

              <dd>
                {p.colors.join(
                  " · "
                )}
              </dd>
            </div>
          )}

          <div>
            <dt>Availability</dt>

            <dd>
              {outOfStock
                ? "Currently unavailable"
                : `${p.stock} pieces available`}
            </dd>
          </div>
        </dl>

        {!outOfStock && (
          <div className="qty-row">
            <button
              onClick={() =>
                setQty(
                  (current) =>
                    Math.max(
                      1,
                      current - 1
                    )
                )
              }
              disabled={
                qty <= 1
              }
              aria-label="Decrease"
              data-testid="qty-minus"
            >
              <Minus size={14} />
            </button>

            <span data-testid="qty-value">
              {qty}
            </span>

            <button
              onClick={() =>
                setQty(
                  (current) =>
                    Math.min(
                      current + 1,
                      Number(
                        p.stock
                      ) || 0
                    )
                )
              }
              disabled={
                qty >=
                (Number(
                  p.stock
                ) || 0)
              }
              aria-label="Increase"
              data-testid="qty-plus"
            >
              <Plus size={14} />
            </button>
          </div>
        )}

        <div className="buy-row">
          <button
            className="button button-dark"
            onClick={
              addToCart
            }
            disabled={
              outOfStock
            }
            data-testid="product-add-to-cart"
          >
            Add to cart{" "}
            <ShoppingBag size={16} />
          </button>

          <button
            className="button button-light"
            onClick={buyNow}
            disabled={
              outOfStock
            }
            data-testid="product-buy-now"
          >
            Buy now{" "}
            <ArrowRight size={16} />
          </button>
        </div>

        <p className="fine-print">
          Complimentary shipping
          information will be confirmed
          at checkout.
        </p>

        {reviews.length > 0 && (
          <section className="reviews">
            <h3>
              What customers say
            </h3>

            {reviews.map(
              (r) => (
                <div
                  className="review"
                  key={
                    r.review_id
                  }
                >
                  <strong>
                    {"★".repeat(
                      r.rating
                    )}
                  </strong>

                  <b>
                    {r.title}
                  </b>

                  <p>
                    {r.body}
                  </p>

                  <small>
                    — {r.author}
                  </small>
                </div>
              )
            )}
          </section>
        )}
      </div>

      {related.length > 0 && (
        <section className="related section">
          <h2>
            You may also{" "}
            <i>like</i>
          </h2>

          <div className="product-grid">
            {related.map(
              (rp) => (
                <ProductCard
                  key={rp.id}
                  product={rp}
                  onAdd={addItem}
                  onToggleWish={() => {}}
                  inWishlist={false}
                />
              )
            )}
          </div>
        </section>
      )}
    </main>
  );
}

/* =========================================================
   CHECKOUT
   ========================================================= */

export function Checkout({
  cart,
  updateQty,
  removeItem,
  clearCart,
  user,
}) {
  const [customer, setCustomer] =
    useState({
      email:
        user?.email || "",
    });

  const [addresses, setAddresses] =
    useState([]);

  const [selectedAddress, setSelectedAddress] =
    useState(null);

  const [showAddressList, setShowAddressList] =
    useState(false);

  const [showAddAddress, setShowAddAddress] =
    useState(false);

  const [placing, setPlacing] =
    useState(false);

  const [loadingAddresses, setLoadingAddresses] =
    useState(false);

  const [settings, setSettings] =
    useState({});

  useEffect(() => {
    setPageSEO({
      title:
        "Checkout | GMK Handicrafts Emporium",
      description:
        "Complete your GMK Handicrafts Emporium order.",
      path: "/checkout",
      noindex: true,
    });
  }, []);

  useEffect(() => {
    apiFetch(
      "/site-settings"
    )
      .then(setSettings)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) {
      setAddresses([]);
      setSelectedAddress(
        null
      );
      return;
    }

    setLoadingAddresses(
      true
    );

    apiFetch(
      "/addresses"
    )
      .then((data) => {
        const list =
          Array.isArray(data)
            ? data
            : data.addresses ||
              [];

        setAddresses(list);

        const defaultAddress =
          list.find(
            (address) =>
              address.is_default
          ) ||
          list[0] ||
          null;

        setSelectedAddress(
          defaultAddress
        );
      })
      .catch(() => {
        setAddresses([]);
        setSelectedAddress(
          null
        );
      })
      .finally(() => {
        setLoadingAddresses(
          false
        );
      });
  }, [user]);

  const subtotal =
    cart.reduce(
      (a, x) =>
        a +
        x.price *
          x.quantity,
      0
    );

  const freeThreshold =
    settings.free_shipping_threshold ||
    0;

  const flatShipping =
    settings.shipping_flat_rate ||
    0;

  const shipping =
    subtotal >=
      freeThreshold &&
    freeThreshold > 0
      ? 0
      : cart.length
      ? flatShipping
      : 0;

  const total =
    subtotal + shipping;

  const formatAddress = (
    address
  ) => {
    if (!address)
      return "";

    return [
      address.house,
      address.area,
      address.landmark,
      address.town,
      address.state,
      address.pincode,
      "India",
    ]
      .filter(Boolean)
      .join(", ");
  };

  const place = async (
    e
  ) => {
    e.preventDefault();

    if (!cart.length) {
      return toast.error(
        "Your collection is empty"
      );
    }

    if (!user) {
      return toast.error(
        "Please sign in before placing an order"
      );
    }

    if (!selectedAddress) {
      return toast.error(
        "Please select a delivery address"
      );
    }

    if (!customer.email) {
      return toast.error(
        "Please enter your email address"
      );
    }

    setPlacing(true);

    try {
      const customerData = {
        name:
          selectedAddress.full_name,
        email:
          customer.email,
        phone:
          selectedAddress.phone,
        address:
          formatAddress(
            selectedAddress
          ),
      };

      const payload = {
        items: cart,
        customer:
          customerData,
        total,
        address:
          selectedAddress,
      };

      const res =
        await apiFetch(
          "/orders",
          {
            method: "POST",
            body: JSON.stringify(
              payload
            ),
          }
        );

      toast.success(
        `Order ${res.order_number} received`,
        {
          description:
            "Payment will be confirmed with you directly.",
        }
      );

      clearCart();
    } catch (err) {
      toast.error(
        err.message ||
          "We couldn't save your order. Please try again."
      );
    } finally {
      setPlacing(false);
    }
  };

  if (!cart.length) {
    return (
      <main className="page checkout">
        <div className="empty">
          <span className="eyebrow">
            Your collection
          </span>

          <h1>
            Your cart is{" "}
            <i>empty.</i>
          </h1>

          <p>
            Explore the atelier and add
            a piece you love.
          </p>

          <Link
            to="/shawls"
            className="button button-dark"
          >
            Explore shawls
            <ArrowRight size={16} />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page checkout">
      <div>
        <span className="eyebrow">
          A considered final step
        </span>

        <h1>
          Complete your{" "}
          <i>collection.</i>
        </h1>

        {!user ? (
          <div className="checkout-form">
            <div className="payment-note">
              <ShieldCheck size={18} />

              <span>
                <b>
                  Sign in to continue
                </b>
                <br />
                Please sign in to use
                your saved delivery
                addresses and place
                your order.
              </span>
            </div>

            <Link
              to="/auth"
              className="button button-dark full"
            >
              Sign in
              <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <form
            onSubmit={place}
            className="checkout-form"
          >
            <label>
              Email address

              <input
                required
                type="email"
                value={
                  customer.email
                }
                onChange={(e) =>
                  setCustomer({
                    ...customer,
                    email:
                      e.target.value,
                  })
                }
                data-testid="checkout-email"
              />
            </label>

            <div className="checkout-address-section">
              <div className="checkout-address-header">
                <span className="eyebrow">
                  Delivery
                </span>

                <h3>
                  Delivery address
                </h3>
              </div>

              {loadingAddresses && (
                <p className="addr-pin-status">
                  Loading your saved
                  addresses...
                </p>
              )}

              {!loadingAddresses &&
                selectedAddress &&
                !showAddressList &&
                !showAddAddress && (
                  <div className="checkout-selected-address">
                    <div className="checkout-address-top">
                      <div>
                        <strong>
                          {
                            selectedAddress.full_name
                          }
                        </strong>

                        {selectedAddress.is_default && (
                          <span className="addr-default-badge">
                            Default
                          </span>
                        )}
                      </div>

                      <button
                        type="button"
                        className="link"
                        onClick={() =>
                          setShowAddressList(
                            true
                          )
                        }
                      >
                        Change
                      </button>
                    </div>

                    <p>
                      {
                        selectedAddress.house
                      }
                      <br />

                      {
                        selectedAddress.area
                      }
                      <br />

                      {selectedAddress.landmark && (
                        <>
                          {
                            selectedAddress.landmark
                          }
                          <br />
                        </>
                      )}

                      {
                        selectedAddress.town
                      }
                      ,{" "}
                      {
                        selectedAddress.state
                      }{" "}
                      {
                        selectedAddress.pincode
                      }
                      <br />

                      India
                      <br />

                      {
                        selectedAddress.phone
                      }
                    </p>
                  </div>
                )}

              {!loadingAddresses &&
                showAddressList &&
                !showAddAddress && (
                  <div className="checkout-address-list">
                    {addresses.map(
                      (address) => (
                        <button
                          type="button"
                          key={
                            address.address_id
                          }
                          className={
                            "checkout-address-option" +
                            (selectedAddress?.address_id ===
                            address.address_id
                              ? " selected"
                              : "")
                          }
                          onClick={() => {
                            setSelectedAddress(
                              address
                            );

                            setShowAddressList(
                              false
                            );
                          }}
                        >
                          <div>
                            <strong>
                              {
                                address.full_name
                              }
                            </strong>

                            {address.is_default && (
                              <span className="addr-default-badge">
                                Default
                              </span>
                            )}
                          </div>

                          <p>
                            {
                              address.house
                            }
                            ,{" "}
                            {
                              address.area
                            }
                            ,{" "}
                            {
                              address.town
                            }
                            ,{" "}
                            {
                              address.state
                            }{" "}
                            {
                              address.pincode
                            }
                          </p>

                          <span>
                            {
                              address.phone
                            }
                          </span>
                        </button>
                      )
                    )}

                    <button
                      type="button"
                      className="button button-light"
                      onClick={() => {
                        setShowAddAddress(
                          true
                        );

                        setShowAddressList(
                          false
                        );
                      }}
                    >
                      + Add a new
                      address
                    </button>
                  </div>
                )}

              {!loadingAddresses &&
                !selectedAddress &&
                !showAddAddress && (
                  <div className="checkout-no-address">
                    <p>
                      You don't have a
                      saved delivery
                      address yet.
                    </p>

                    <button
                      type="button"
                      className="button button-dark"
                      onClick={() =>
                        setShowAddAddress(
                          true
                        )
                      }
                    >
                      Add a new
                      address
                      <ArrowRight size={16} />
                    </button>
                  </div>
                )}

              {showAddAddress && (
                <div className="checkout-new-address">
                  <AddressForm
                    showCancel={Boolean(
                      selectedAddress
                    )}
                    onCancel={() =>
                      setShowAddAddress(
                        false
                      )
                    }
                    onSaved={(saved) => {
                      setAddresses(
                        (current) => [
                          ...current,
                          saved,
                        ]
                      );

                      setSelectedAddress(
                        saved
                      );

                      setShowAddAddress(
                        false
                      );

                      setShowAddressList(
                        false
                      );
                    }}
                  />
                </div>
              )}
            </div>

            <div className="payment-note">
              <ShieldCheck size={18} />

              <span>
                <b>
                  Online payment not
                  yet enabled
                </b>
                <br />

                Your order details are
                safely recorded. We'll
                confirm payment and
                delivery with you
                directly.
              </span>
            </div>

            <button
              className="button button-dark full"
              disabled={
                placing ||
                loadingAddresses ||
                !selectedAddress
              }
              data-testid="place-order-button"
            >
              {placing
                ? "Placing order…"
                : "Place order request"}

              <ArrowRight size={16} />
            </button>
          </form>
        )}
      </div>

      <aside className="summary">
        <span className="eyebrow">
          Your selection
        </span>

        <h2>
          Order summary
        </h2>

        {cart.map((x) => (
          <div
            className="summary-item"
            key={x.id}
            data-testid={`cart-item-${x.id}`}
          >
            <img
              src={mainImage(x)}
              alt=""
            />

            <span>
              {x.name}

              <small>
                <button
                  type="button"
                  onClick={() =>
                    updateQty(
                      x.id,
                      x.quantity -
                        1
                    )
                  }
                  aria-label="Decrease"
                  data-testid={`cart-dec-${x.id}`}
                >
                  <Minus size={10} />
                </button>

                Qty {x.quantity}

                <button
                  type="button"
                  onClick={() => {
                    if (
                      x.stock !==
                        null &&
                      x.stock !==
                        undefined
                    ) {
                      if (
                        x.quantity >=
                        Number(
                          x.stock
                        )
                      ) {
                        toast.info(
                          `Only ${
                            x.stock
                          } piece${
                            Number(
                              x.stock
                            ) === 1
                              ? ""
                              : "s"
                          } available.`
                        );

                        return;
                      }
                    }

                    updateQty(
                      x.id,
                      x.quantity +
                        1
                    );
                  }}
                  disabled={
                    x.stock !==
                      null &&
                    x.stock !==
                      undefined &&
                    x.quantity >=
                      Number(
                        x.stock
                      )
                  }
                  aria-label="Increase"
                  data-testid={`cart-inc-${x.id}`}
                >
                  <Plus size={10} />
                </button>

                <button
                  type="button"
                  onClick={() =>
                    removeItem(
                      x.id
                    )
                  }
                  aria-label="Remove"
                  data-testid={`cart-remove-${x.id}`}
                >
                  <Trash2 size={10} />
                </button>
              </small>
            </span>

            <b>
              {money(
                x.price *
                  x.quantity
              )}
            </b>
          </div>
        ))}

        <div className="summary-row">
          <span>
            Subtotal
          </span>

          <b>
            {money(subtotal)}
          </b>
        </div>

        <div className="summary-row">
          <span>
            Shipping
          </span>

          <b>
            {shipping
              ? money(
                  shipping
                )
              : "Free"}
          </b>
        </div>

        <div className="total">
          <span>Total</span>

          <strong>
            {money(total)}
          </strong>
        </div>
      </aside>
    </main>
  );
}

/* =========================================================
   ABOUT
   ========================================================= */

export function About() {
  const [page, setPage] =
    useState(null);

  useEffect(() => {
    apiFetch(
      "/content-pages/about"
    )
      .then(setPage)
      .catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;

    async function loadSEO() {
      const seo =
        await fetchPublicSEO(
          "page",
          "about"
        );

      if (!active) return;

      applyManagedSEO(
        seo,
        {
          title:
            "Our Story | GMK Handicrafts Emporium",
          description:
            "Discover the story behind GMK Handicrafts Emporium and our appreciation for the timeless craft and heritage of Kashmir.",
          path: "/about",
        }
      );
    }

    loadSEO();

    return () => {
      active = false;
    };
  }, []);

  return (
    <main className="page about">
      <div className="about-hero">
        <span className="eyebrow">
          Our story
        </span>

        <h1>
          Made slowly.
          <br />
          <i>
            Held onto dearly.
          </i>
        </h1>
      </div>

      <div className="about-body">
        <img
          src="https://images.unsplash.com/photo-1707978932202-751b08324daf?auto=format&fit=crop&w=1000&q=85"
          alt="Kashmiri artisan craft"
        />

        <div>
          <span className="eyebrow">
            The craft of Kashmir
          </span>

          <h2>
            {page?.title ||
              "A collection with a human hand in every thread."}
          </h2>

          <div
            dangerouslySetInnerHTML={{
              __html:
                page?.body_html ||
                "<p>Our story is being written.</p>",
            }}
          />
        </div>
      </div>
    </main>
  );
}

/* =========================================================
   CONTACT
   ========================================================= */

export function Contact() {
  const [settings, setSettings] =
    useState({});

  useEffect(() => {
    apiFetch(
      "/site-settings"
    )
      .then(setSettings)
      .catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;

    async function loadSEO() {
      const seo =
        await fetchPublicSEO(
          "page",
          "contact"
        );

      if (!active) return;

      applyManagedSEO(
        seo,
        {
          title:
            "Contact | GMK Handicrafts Emporium",
          description:
            "Contact GMK Handicrafts Emporium for private appointments, collection questions, gifting help and Kashmiri handicraft enquiries.",
          path: "/contact",
        }
      );
    }

    loadSEO();

    return () => {
      active = false;
    };
  }, []);

  return (
    <main className="page contact">
      <span className="eyebrow">
        We're here to help
      </span>

      <h1>
        Start a{" "}
        <i>conversation.</i>
      </h1>

      <p>
        For private appointments,
        collection questions or help
        choosing a gift, leave us a
        note.
      </p>

      <form
        className="contact-form"
        onSubmit={(e) => {
          e.preventDefault();

          toast.success(
            "Thank you — we'll be in touch soon"
          );
        }}
      >
        <input
          placeholder="Your name"
          required
          data-testid="contact-name"
        />

        <input
          type="email"
          placeholder="Email address"
          required
          data-testid="contact-email"
        />

        <textarea
          placeholder="How can we help?"
          required
          data-testid="contact-message"
        />

        <button
          className="button button-dark"
          data-testid="contact-submit"
        >
          Send message{" "}
          <ArrowRight size={16} />
        </button>
      </form>

      <div className="contact-details">
        {settings.phone && (
          <span>
            <small>
              Phone
            </small>

            {settings.phone}
          </span>
        )}

        {settings.email && (
          <span>
            <small>
              Email
            </small>

            {settings.email}
          </span>
        )}

        {settings.whatsapp && (
          <span>
            <small>
              WhatsApp
            </small>

            {settings.whatsapp}
          </span>
        )}

        {settings.address && (
          <span>
            <small>
              Location
            </small>

            {settings.address}
          </span>
        )}
      </div>

      {settings.map_embed && (
        <div
          className="map-embed"
          dangerouslySetInnerHTML={{
            __html:
              settings.map_embed,
          }}
        />
      )}
    </main>
  );
}

/* =========================================================
   STATIC PAGES
   ========================================================= */

export function StaticPage() {
  const { slug } =
    useParams();

  const [page, setPage] =
    useState(null);

  const [err, setErr] =
    useState(false);

  useEffect(() => {
    apiFetch(
      `/content-pages/${slug}`
    )
      .then(setPage)
      .catch(() =>
        setErr(true)
      );
  }, [slug]);

  useEffect(() => {
    if (!page) return;

    let active = true;

    async function loadSEO() {
      const title =
        page.title ||
        "GMK Handicrafts Emporium";

      const description =
        page.body_html
          ?.replace(
            /<[^>]+>/g,
            " "
          )
          ?.replace(
            /\s+/g,
            " "
          )
          ?.trim()
          ?.slice(0, 300) ||
        "Learn more about GMK Handicrafts Emporium.";

      const seo =
        await fetchPublicSEO(
          "page",
          slug
        );

      if (!active) return;

      applyManagedSEO(
        seo,
        {
          title:
            `${title} | GMK Handicrafts Emporium`,
          description,
          path:
            `/pages/${slug}`,
        }
      );
    }

    loadSEO();

    return () => {
      active = false;
    };
  }, [page, slug]);

  if (err) {
    return (
      <main className="page about">
        <h1>
          Page not found
        </h1>
      </main>
    );
  }

  if (!page) {
    return (
      <div className="loading">
        Loading…
      </div>
    );
  }

  return (
    <main className="page static-page">
      <span className="eyebrow">
        GMK Handicrafts
      </span>

      <h1>
        {page.title}
      </h1>

      <div
        className="static-body"
        dangerouslySetInnerHTML={{
          __html:
            page.body_html,
        }}
      />
    </main>
  );
}
